import ClientChecklistCell from "./ClientChecklistCell";
import { useClientChecklists } from "../_hooks/useClientChecklists";
import { Box, Chip, Divider, Stack, Typography } from "@mui/material";
import type { ReactNode } from "react";
import dayjs from "dayjs";
import states from "us-state-codes";
import { isActiveRole, isUserRole } from "../../../utils/methods/storeFunc";
import { USER_ROLES } from "../../../staticData/userRoles";
import { eventDayRange } from "../../../utils/methods/calendarLayout";
import CoordinatorAvatars from "./CoordinatorAvatars";
import { calendarStatusColor } from "./calendarStatusColors";
import { eventStatusLabel, isCancelledEvent } from "../../../utils/methods/eventPresentation";
import { eventTypeBadgeLabel } from "./eventTypes";
import { getRowStrips } from "../../../utils/methods/eventUtils";
import type { CalendarEvent } from "../_hooks/useCalendarEvents";

const formatDay = (day: string) => dayjs(day).format("ddd MM/DD/YYYY");

const fullName = (u: Record<string, unknown> | null | undefined): string =>
  u && typeof u === "object"
    ? `${(u.firstName as string) || ""} ${(u.lastName as string) || ""}`.trim()
    : "";

const clientName = (ev: CalendarEvent): string => {
  const u = ev.userId;
  if (!u || typeof u !== "object") return "";
  return (u.companyName as string) || fullName(u) || (u.email as string) || "";
};

const days = (n: unknown): string =>
  typeof n === "number" ? `${n} ${Math.abs(n) === 1 ? "day" : "days"}` : "—";

const money = (n: unknown): string =>
  typeof n === "number" ? `$ ${n.toFixed(2)}` : "—";

// The table shows the state CODE (venueState is stored as the full name).
const stateCode = (code: unknown): string => {
  if (typeof code !== "string" || !code) return "";
  return (
    (
      states as { getStateCodeByStateName: (s: string) => string | null }
    ).getStateCodeByStateName(code) || code
  );
};

type Row = { label: string; value: ReactNode };

/**
 * Everything the dashboard table shows for an event, as a label/value list for
 * the calendar bar tooltip. Follows the same role gating as `getColumns()`.
 */
export default function CalendarEventDetails({ ev }: { ev: CalendarEvent }) {
  const range = eventDayRange(ev);
  const status = eventStatusLabel(ev, "—");
  const color = calendarStatusColor(status);
  const coordinators = Array.isArray(ev.assignedCoordinatorId)
    ? ev.assignedCoordinatorId
    : [];
  const isClient = isActiveRole(USER_ROLES.CLIENT);
  const clientChecklists = useClientChecklists([ev._id], !isClient);
  const isCoordinator = isActiveRole(USER_ROLES.COORDINATOR);
  const hidesMoney = isUserRole(USER_ROLES.CLIENT, USER_ROLES.COORDINATOR);
  const strips: { color: string; reason: string }[] = isUserRole(
    USER_ROLES.SUPERADMIN,
    USER_ROLES.ADMIN,
  )
    ? getRowStrips(ev)
    : [];

  const venue = [
    ev.venue,
    ev.venueAddressLine1,
    ev.venueCity,
    ev.venueState,
    ev.venueZipCode,
  ]
    .filter(Boolean)
    .join(", ");

  const pl =
    ev.PL != null && parseFloat(String(ev.PL)) >= 0 ? String(ev.PL) : "N/A";
  const plAmount =
    typeof ev.PLAmount === "number" ? ` (${money(ev.PLAmount)})` : "";

  const rows: (Row | null)[] = [
    { label: "State", value: stateCode(ev.venueState) || "—" },
    { label: "City", value: ev.venueCity || "—" },
    isClient ? null : { label: "Client", value: clientName(ev) || "—" },
    { label: "Venue", value: venue || "—" },
    isClient || !eventTypeBadgeLabel(ev.lifecycle)
      ? null
      : { label: "Stage", value: eventTypeBadgeLabel(ev.lifecycle) },
    isClient ? null : { label: "Event Status", value: ev.sowStatus || "—" },
    { label: "Start Date", value: range ? formatDay(range.start) : "—" },
    { label: "End Date", value: range ? formatDay(range.end) : "—" },
    { label: "Starts In", value: days(ev.daysTillStart) },
    { label: "Ends In", value: days(ev.daysTillEnd) },
    { label: "Contact", value: fullName(ev.contactInformationId) || "—" },
    isClient ? null : { label: "Client onboarding", value: <ClientChecklistCell state={clientChecklists.get(ev._id)} onRefresh={clientChecklists.refresh} /> },
    {
      label: "Notes",
      value: ev.eventNotes
        ? String(ev.eventNotes).length > 140
          ? `${String(ev.eventNotes).slice(0, 140)}…`
          : String(ev.eventNotes)
        : "—",
    },
    isCoordinator
      ? null
      : {
          label: "Estimate",
          value: ev.submittedEstimate?._id
            ? ev.submittedEstimate?.estimateNumber || "N/A"
            : "N/A",
        },
    { label: "Client's PO#", value: ev.eventClientSentPO || "N/A" },
    isCoordinator
      ? null
      : {
          label: "Deposit Invoice",
          value: ev.depositInvoice?._id
            ? ev.depositInvoice?.depositInvoiceNumber || "N/A"
            : "N/A",
        },
    isCoordinator
      ? null
      : {
          label: "Final Invoice",
          value: ev.finalInvoice?.invoiceNumber || "N/A",
        },
    hidesMoney ? null : { label: "P&L", value: `${pl}${plAmount}` },
    hidesMoney
      ? null
      : { label: "Invoiced Amount", value: money(ev.invoiceAmount) },
  ];

  return (
    <Box sx={{ p: 0.5, maxWidth: 440 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
        <Chip
          size="small"
          label={status}
          sx={{ bgcolor: color.bg, color: color.fg, fontWeight: 700 }}
        />
        <Typography sx={{ fontWeight: 700, textDecoration: isCancelledEvent(ev) ? "line-through" : "none" }}>{ev.eventName}</Typography>
      </Stack>
      {strips.length ? (
        <Stack spacing={0.25} sx={{ mb: 0.5 }}>
          {strips.map((st, i) => (
            <Stack key={i} direction="row" spacing={0.75} alignItems="center">
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: "2px",
                  bgcolor: st.color,
                  flex: "0 0 auto",
                }}
              />
              <Typography variant="caption">{st.reason}</Typography>
            </Stack>
          ))}
        </Stack>
      ) : null}
      <Divider sx={{ borderColor: "rgba(209,117,182,0.5)", mb: 0.5 }} />
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "auto minmax(0, 1fr)",
          columnGap: "0.75rem",
          rowGap: "2px",
          alignItems: "baseline",
        }}
      >
        {rows
          .filter((r): r is Row => r !== null)
          .map((r) => (
            <Box key={r.label} sx={{ display: "contents" }}>
              <Typography
                variant="caption"
                sx={{ opacity: 0.75, whiteSpace: "nowrap" }}
              >
                {r.label}
              </Typography>
              <Typography component="div" variant="body2" sx={{ wordBreak: "break-word" }}>
                {r.value}
              </Typography>
            </Box>
          ))}
        <Typography variant="caption" sx={{ opacity: 0.75, pt: 0.5 }}>
          Coordinators
        </Typography>
        <Box sx={{ pt: 0.5 }}>
          <CoordinatorAvatars coordinators={coordinators} slots={8} />
        </Box>
      </Box>
    </Box>
  );
}
